/**
 * The register: every case, one screen, no controls.
 *
 * Rebuilt to the frozen screen-2 mockup (16 Aug 2026) as amended by the
 * rulings of 17 Aug 2026:
 *
 * **(a) No search, no sort control, no pagination.** A factory holds 15–20
 * live cases and closed ones are not uploaded, so the whole register is
 * visible at once, in the server's own order — the client does not re-rank it.
 * Because the register fits on one screen, a row may carry more rather than
 * less: the case's state travels on the row, so a withdrawn case is
 * distinguishable from a live one, not merely reachable.
 *
 * **(b) A wait row shows its state and offers no action.** Who it waits on
 * comes off the row (`next_note`), composed on the server.
 *
 * **(c) There is no "out" class.** D18–D20 are external and in scope, so a
 * CAT matter reads as awaited, not as outside the tool.
 *
 * What each case is on is **decided on the server** — the row carries the
 * case's one next-action card (docs/19 A3), the same object the case page
 * opens on, so a client never works out whether to draw *Draft it* for itself.
 * Stage grouping and every title come off the rows, which
 * come off each case's pinned process version; there is no list of four
 * stages in this file (stop list 6).
 */

import { Link } from 'react-router-dom'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { CaseIdentifierView, CaseSummary } from '../api/client'
import { ClockPill, ErrorEnvelope } from '../components/bits'
import { readDate } from '../lib/period'
import { isCourtDocument } from '../lib/court'
import { readDay } from '../lib/blocks'
import { useSession } from '../lib/session'
import { draftNow, situationWords, urgentElsewhere } from './case/acting'

export function Queue() {
  const { user } = useSession()
  // Null role is unknown, never "officer" (the field's own contract): the
  // reading surfaces render either way, and the acting ones — the two intake
  // doors and Draft it — wait until a role is actually known.
  const cgm = user?.role === 'cgm'
  const canAct = user?.role != null && !cgm
  const cases = useQuery({ queryKey: ['cases'], queryFn: () => api.listCases() })
  // The identifier line of every row (docs/24 item 5) — English, built on the
  // server from his record and the case record, never composed here.
  const identifiers = useQuery({
    queryKey: ['case-identifiers'],
    queryFn: () => api.listCaseIdentifiers(),
  })
  const lines = new Map((identifiers.data ?? []).map((row) => [row.case_id, row]))
  const all = cases.data ?? []
  /* **Closed work is still in the register; it is not still in the way.**
   *
   * A register holds every proceeding, and a withdrawn case does not stop
   * being one — ruling (a) is that this screen carries no filters, because an
   * empty list should mean an empty register and never a filter somebody left
   * on. That holds for the live work. It does not hold for rows whose whole
   * content is that they are over: four withdrawn intakes above the two cases
   * an officer is actually working is a register that hides its own subject.
   *
   * So: a toggle the officer owns, off by default, with the count of what it
   * is holding back beside it — never a silent filter, and one click from the
   * whole register. Nothing is deleted and nothing is unreachable (rule 3). */
  const [showClosed, setShowClosed] = useState(false)
  const closed = all.filter((row) => row.state !== 'OPEN')
  const live = showClosed ? all : all.filter((row) => row.state === 'OPEN')

  /* **Where a case has reached, as a way into the register.**
   *
   * The stages come off the published process — never a list of titles typed
   * into this file (stop list 6), so a factory on a corrected version gets its
   * own words without anyone editing a screen — and **all of them are offered,
   * including the empty ones.**
   *
   * Built from the cases present instead, the tabs were a list of where cases
   * happen to be: with nothing past the inquiry, there was no way to ask for
   * the Tribunal at all, and the honest answer to that question — none yet — is
   * one an officer is entitled to. A stage with no cases shows its zero. */
  const process = useQuery({ queryKey: ['process'], queryFn: () => api.getProcess() })
  const stageTabs = (process.data?.stages ?? [])
    .filter((entry) => entry.in_scope)
    .sort((a, b) => a.n - b.n)
  const [stage, setStage] = useState<string | null>(null)
  const rows = stage ? live.filter((row) => row.stage === stage) : live

  return (
    <div className="page">
      <header className="pagehead-row">
        <h1>Cases</h1>
        {/* **The action belongs to the heading, not to a strip of its own.**
            With the subtitle gone, *Add a case* and the count sat alone in a
            bar between the title and the register, reading as a toolbar with
            nothing in it. Beside the heading they are plainly this page's own
            action and this page's own count. */}
        {canAct ? (
          <div className="pagehead-actions">
            <span className="small muted">
              {rows.length} case{rows.length === 1 ? '' : 's'}
            </span>
            {closed.length ? (
              <button
                type="button"
                className="btn quiet"
                data-testid="toggle-closed"
                onClick={() => setShowClosed((was) => !was)}
              >
                {showClosed ? `Hide ${closed.length} closed` : `Show ${closed.length} closed`}
              </button>
            ) : null}
            <Link className="btn" to="/cases/new">
              Add a case
            </Link>
          </div>
        ) : null}
        {/* **Only the CGM gets a line here.** The officer's said that this
            register is one factory's, which the masthead two centimetres
            above already says, and that it holds every proceeding whatever
            stage it has reached — which is what a register is. The CGM's
            says something the screen does not otherwise: that nothing on it
            changes a case. */}
        {cgm ? (
          <p className="muted small">
            {user?.unit_name ? `${user.unit_name} — ` : ''}
            read and ask. Nothing on this page changes a case.
          </p>
        ) : null}
      </header>

      {cgm && rows.length > 0 ? <FactorySummary rows={rows} /> : null}

      {cases.error ? <ErrorEnvelope error={cases.error} /> : null}

      {stageTabs.length > 1 ? (
        <div className="stage-tabs" data-testid="stage-tabs">
          <button
            type="button"
            className={stage === null ? 'on' : undefined}
            onClick={() => setStage(null)}
          >
            All
            <span className="cnt mono">{live.length}</span>
          </button>
          {stageTabs.map((entry) => {
            const n = live.filter((r) => r.stage === entry.id).length
            return (
              <button
                key={entry.id}
                type="button"
                className={`${stage === entry.id ? 'on' : ''}${n === 0 ? ' none' : ''}`.trim()}
                data-testid={`stage-tab-${entry.id}`}
                onClick={() => setStage(stage === entry.id ? null : entry.id)}
              >
                {entry.title}
                <span className="cnt mono">{n}</span>
              </button>
            )
          })}
        </div>
      ) : null}

      {cases.isLoading ? (
        <div className="empty">Reading the register…</div>
      ) : all.length === 0 ? (
        <EmptyRegister canAct={canAct} />
      ) : rows.length === 0 && stage ? (
        <div className="empty">
          No open case is at that stage.{' '}
          <button type="button" onClick={() => setStage(null)}>
            Show the whole register
          </button>
        </div>
      ) : rows.length === 0 ? (
        /* **A register held back by a toggle is not an empty register.**
           `EmptyRegister` says the register holds nothing and offers the door
           to a first case; shown over a register whose every row is closed, it
           would be a plain untruth with a button under it. */
        <div className="empty">
          Nothing open. {closed.length} closed {closed.length === 1 ? 'case is' : 'cases are'}{' '}
          hidden.
        </div>
      ) : (
        <div className="card">
          <Register rows={rows} canAct={canAct} lines={lines} />
        </div>
      )}
    </div>
  )
}

/**
 * The whole register never renders an empty screen by accident: with no
 * filters (ruling a) an empty list means an empty register, and eight of the
 * nine factories will see this before they see anything else. The words
 * change by role — a CGM landing here needs to know the tool is not broken,
 * and has no buttons because there is nothing for them to do.
 */
function EmptyRegister({ canAct }: { canAct: boolean }) {
  return (
    <div className="empty" data-testid="empty-register">
      <p>
        <strong>No cases yet.</strong>
      </p>
      {!canAct ? (
        <p className="muted">
          Nothing has been added at this factory. Cases appear here as the disciplinary section
          adds them.
        </p>
      ) : (
        <>
          {/* **Nothing here but the heading.** *Add a case* is in the toolbar
              above, whether the register is empty or full, so it neither
              repeats nor moves. The sentence that explained the one door went
              with it: an officer who has been taught this screen once does not
              need the door described, and an empty register that talks at
              length reads as though something has gone wrong. The `!canAct`
              branch above keeps its line, because a CGM seeing an empty
              register is owed the reason. */}
        </>
      )}
    </div>
  )
}

/**
 * The factory summary the CGM sees above the list — stat tiles, not a chart.
 *
 * Counts are over the **whole register this CGM can see**, which since the
 * factory boundary is exactly their own factory's — the server scopes the
 * list to the signed-in officer's unit and row security backs it, so these
 * are one factory's numbers and can never silently become nine factories'.
 * Stage tiles are built from the stages the rows themselves report — a case
 * pinned to a five-stage version brings its fifth tile with it.
 */
function FactorySummary({ rows }: { rows: CaseSummary[] }) {
  const byStage = new Map<string, { title: string; count: number }>()
  for (const row of rows) {
    if (!row.stage) continue
    const entry = byStage.get(row.stage) ?? { title: row.stage_title ?? row.stage, count: 0 }
    entry.count += 1
    byStage.set(row.stage, entry)
  }
  const stages = [...byStage.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  // Read off each case's card: the section's move where the card is something
  // to do or needs a paper first, someone else's where it waits.
  const situation = (row: CaseSummary) =>
    row.next_action?.situation ??
    (row.next_kind === 'ours' ? 'do' : row.next_kind === 'wait' ? 'wait' : 'none')
  const ours = rows.filter((row) => ['do', 'needs'].includes(situation(row))).length
  const wait = rows.filter((row) => situation(row) === 'wait').length

  return (
    <div className="register-summary" data-testid="factory-summary">
      <div className="tiles">
        {stages.map(([id, entry]) => (
          <div className="tile" key={id}>
            <div className="n">{entry.count}</div>
            <div className="l">{entry.title}</div>
          </div>
        ))}
      </div>
      <div className="tiles two">
        <div className="tile">
          <div className="n">{ours}</div>
          <div className="l">waiting on the section</div>
          <div className="s">a document to draft, or a paper the office has to get</div>
        </div>
        <div className="tile">
          <div className="n">{wait}</div>
          <div className="l">waiting on someone else</div>
          <div className="s">the charged officer, an enquiring officer, or counsel</div>
        </div>
      </div>
    </div>
  )
}

function Register({
  rows,
  canAct,
  lines,
}: {
  rows: CaseSummary[]
  canAct: boolean
  lines: Map<string, CaseIdentifierView>
}) {
  return (
    <table>
      <thead>
        <tr>
          <th>File number</th>
          <th>Charged officer</th>
          <th>Stage</th>
          <th>Last activity</th>
          <th>What is due next</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <RegisterRow key={row.id} row={row} canAct={canAct} identity={lines.get(row.id)} />
        ))}
      </tbody>
    </table>
  )
}

//: How a case that is no longer open reads on its row. Withdrawal is a state
//: and nothing is deleted — so a dead case must be distinguishable at a
//: glance, not merely reachable.
const STATE_WORDS: Record<string, string> = {
  CLOSED: 'Closed',
  DROPPED: 'Dropped',
  WITHDRAWN: 'Withdrawn',
}

function RegisterRow({
  row,
  canAct,
  identity,
}: {
  row: CaseSummary
  canAct: boolean
  identity: CaseIdentifierView | undefined
}) {
  // English leads with the Hindi beneath — and a row with no English name
  // leads with the Hindi one rather than an empty line. Hindi is the record;
  // `name_en` is nullable and always will be.
  const lead = row.employee_name_en?.trim() ? row.employee_name_en : row.employee_name_hi
  const beneath = row.employee_name_en?.trim() ? row.employee_name_hi : null
  // **An intake nobody has confirmed has no number** (Phase A): it says so, and
  // its row goes back to the intake rather than to a case page with nothing on it.
  const numbered = Boolean(row.file_number)

  return (
    <tr data-testid="case-row">
      <td>
        {numbered ? (
          <Link to={`/cases/${row.id}`} className="mono deva">
            {row.file_number}
          </Link>
        ) : (
          <Link
            to={row.state === 'OPEN' ? `/cases/new?case=${row.id}` : `/cases/${row.id}`}
            data-testid="not-yet-numbered"
          >
            {identity?.file_number_label ?? 'Not yet numbered'}
          </Link>
        )}
        {identity ? (
          <div className="small" data-testid="case-identifier-line">
            {/* The server's own line; on a row whose number is not yet taken
                the link above already says so, and the rest of the line follows. */}
            {numbered ? identity.line : identity.after_number}
          </div>
        ) : null}
        {row.state !== 'OPEN' ? (
          <div>
            <span className="chip neutral" data-testid="case-state">
              {STATE_WORDS[row.state] ?? row.state}
            </span>
          </div>
        ) : null}
      </td>
      <td>
        <span className={beneath ? undefined : 'deva'}>{lead}</span>
        {beneath ? <div className="small muted deva">{beneath}</div> : null}
        {row.employee_staff_number ? (
          <div className="small faint">
            Ticket <span className="mono">{row.employee_staff_number}</span>
          </div>
        ) : null}
      </td>
      <td>
        {row.stage_title ?? '—'}
        {/* The route (major, minor or dropped) is a decision of the first stage.
            A case that is here for its court documents has none to decide, so
            the line is left off it — a route that IS on the record still shows. */}
        {row.route || !isCourtDocument(row.next_action?.document) ? (
          <div className="small faint">
            {row.route
              ? (row.route_label ?? <span className="mono">{row.route}</span>)
              : 'route not yet decided'}
          </div>
        ) : null}
      </td>
      <td>
        {/* A date is a fact and is shown; elapsed time is never computed. A
            case with no formal step yet falls back to when it was opened, and
            says so rather than showing a blank cell. */}
        <span className="mono small">
          {readDate(row.last_activity_at ? readDay(row.last_activity_at) : row.opened_on)}
        </span>
        <div className="small muted">{row.last_activity_label ?? 'case opened'}</div>
      </td>
      <td>
        <NextCell row={row} canAct={canAct} />
      </td>
    </tr>
  )
}

/**
 * **The case's one card, the same one its page opens on** (docs/19 A3). The
 * situation, the subject, and the period it is about in the one wording — and,
 * for the officer, *Draft it* where the card's own first act is a draft. The
 * register and the case page cannot disagree about what a case is waiting for,
 * because they read one answer.
 *
 * A row from a server older than the card falls back to the split the register
 * used to draw: `ours` with its document, `wait` and `none` with the sentence.
 */
function NextCell({ row, canAct }: { row: CaseSummary; canAct: boolean }) {
  const card = row.next_action
  if (card) {
    const first = (card.offers ?? []).find((offer) => offer.primary)
    const drafts = first && (first.act === 'draft' || first.act === 'review') && first.document
    return (
      <div data-testid={`next-${card.situation}`} data-step={card.step ?? ''}>
        <div className="small muted">{situationWords(card.situation)}</div>
        <div>{card.subject}</div>
        {card.clock ? (
          <div className="small">
            <ClockPill from={card.clock} />{' '}
            <span className="muted">
              {card.clock.label}
              {card.clock.authority ? ` · ${card.clock.authority}` : ''}
            </span>
          </div>
        ) : null}
        {/* **The most urgent period, wherever the card is.** A card held on an
            act the record lacks carries no clock of its own, and an overdue
            period behind it must not vanish from the register (compliance
            review, 26 Sep 2026). Shown only when it is not the card's own,
            and only once it is due soon or overdue. */}
        {urgentElsewhere(row) ? (
          <div className="small" data-testid="next-urgent-period">
            <ClockPill
              from={{
                state: row.worst_clock_state,
                days_left: row.days_left,
                blocked_reason: row.worst_clock_reason,
              }}
            />{' '}
            <span className="muted">
              {row.worst_clock_label}
              {row.worst_clock_authority ? ` · ${row.worst_clock_authority}` : ''}
            </span>
          </div>
        ) : null}
        {canAct && drafts ? (
          // The same card as the case page's, so *Draft it* does the same
          // thing from here: it starts the draft (see `draftNow`).
          <Link
            className="btn"
            to={`/cases/${row.id}/documents/${first.document}`}
            state={first.act === 'draft' ? draftNow(first.anyway, first.purpose) : undefined}
            data-testid={`draft-${first.document}`}
          >
            {first.act === 'review' ? 'Open the draft' : 'Draft it'}
          </Link>
        ) : null}
      </div>
    )
  }
  if (row.next_kind === 'ours' && row.next_document_type) {
    return (
      <div data-testid="next-ours">
        <div>{row.next_document_short_title ?? row.next_document_title}</div>
        {canAct ? (
          <Link
            className="btn"
            to={`/cases/${row.id}/documents/${row.next_document_type}`}
            data-testid={`draft-${row.next_document_type}`}
          >
            Draft it
          </Link>
        ) : null}
      </div>
    )
  }
  if (row.next_note) {
    return <span className="small muted">{row.next_note}</span>
  }
  return <span className="muted">—</span>
}
